/**
 * Детерминированный визуал категории.
 *
 * Категория не хранит картинку: цвет и глиф выводятся из имени. Одинаковое
 * имя всегда даёт одинаковый визуал на всех устройствах, без сети и файлов.
 */

export interface CategoryGradient {
  /** Начальный цвет градиента. */
  from: string
  /** Конечный цвет градиента. */
  to: string
}

/** Число hue-семей: 8 значений с шагом 45°. */
export const CATEGORY_HUE_COUNT = 8

/**
 * 16 градиентов = 8 hue-семей × 2 уровня светлоты.
 *
 * Hue: 20, 65, 110, 155, 200, 245, 290, 335 — шаг 45°, перцептивная разница
 * ΔE ≈ 0.088. Это выше комфортного порога 0.06; при 16 оттенках на одном
 * кольце (шаг 22.5°) ΔE падал до 0.045, и соседние цвета казались одинаковыми.
 *
 * Уровни: светлый (`L` 0.64 → 0.58) и глубокий (`L` 0.51 → 0.44). Между
 * уровнями разрыв 0.07 — они не перекрываются, центры разнесены на 0.135
 * (ΔE ≈ 0.135). Контраст белой иконки — 3.22–8.33:1, норма WCAG 1.4.11
 * (3:1) выполнена на всех стопах.
 *
 * Семантические `#DC2626`/`#16A34A` в палитру не входят, чтобы бейджи не
 * путались с категориями.
 *
 * Обоснование: `docs/UX-CATEGORY-PALETTE.md` и
 * `docs/UX-CATEGORY-PALETTE-SPACING.md`.
 */
export const CATEGORY_PALETTE: CategoryGradient[] = [
  // Светлый уровень: L 0.64 → 0.58
  { from: '#C17273', to: '#B25D5F' }, // terracotta
  { from: '#B67F46', to: '#A66B29' }, // amber
  { from: '#909145', to: '#7F8026' }, // olive
  { from: '#569E71', to: '#3B8D5D' }, // green
  { from: '#279EA4', to: '#008D94' }, // teal
  { from: '#5492C5', to: '#3A80B7' }, // blue
  { from: '#8B81C5', to: '#7A6EB7' }, // violet
  { from: '#B274A4', to: '#A26094' }, // rose
  // Глубокий уровень: L 0.51 → 0.44, разрыв с светлым 0.07
  { from: '#9F4549', to: '#8C2C33' }, // terracotta deep
  { from: '#945500', to: '#813E00' }, // amber deep
  { from: '#6B6B00', to: '#585700' }, // olive deep
  { from: '#127946', to: '#006630' }, // green deep
  { from: '#007981', to: '#00656F' }, // teal deep
  { from: '#156BA6', to: '#005694' }, // blue deep
  { from: '#6658A5', to: '#534294' }, // violet deep
  { from: '#8F4981', to: '#7C316E' } // rose deep
]

/** Количество минималистичных глифов в наборе. */
export const CATEGORY_GLYPH_COUNT = 16

export interface CategoryVisual {
  /** Индекс градиента в палитре. */
  colorKey: number
  /** Индекс глифа. */
  glyph: number
}

/** FNV-1a: стабильный 32-битный хеш строки. */
export function hashString(value: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/**
 * Визуал категории. Ручные `colorKey`/`glyph` имеют приоритет над хешем.
 *
 * `parent` — имя верхней категории (отдела). Если задано, цвет берётся из
 * отдела, а иконка — из листа. Так соседи внутри отдела получают один цвет
 * (визуальная группировка), а подкатегории различаются иконкой. Без `parent`
 * цвет считается по имени — обратная совместимость.
 */
export function categoryVisual(
  name: string,
  colorKey?: number | null,
  glyph?: number | null,
  parent?: string | null
): CategoryVisual {
  const leafHash = hashString(name.trim().toLowerCase())
  const colorSource = parent?.trim()
    ? parent.trim().toLowerCase()
    : name.trim().toLowerCase()
  const colorHash = hashString(colorSource)

  return {
    colorKey:
      colorKey != null && colorKey >= 0
        ? colorKey % CATEGORY_PALETTE.length
        : colorHash % CATEGORY_PALETTE.length,
    glyph:
      glyph != null && glyph >= 0
        ? glyph % CATEGORY_GLYPH_COUNT
        : Math.floor(leafHash / CATEGORY_PALETTE.length) % CATEGORY_GLYPH_COUNT
  }
}

/** Градиент категории (для плиток товаров и фонов). */
export function categoryGradient(
  name: string,
  colorKey?: number | null,
  parent?: string | null
): CategoryGradient {
  return CATEGORY_PALETTE[categoryVisual(name, colorKey, null, parent).colorKey]
}
